import {Injectable} from '@angular/core';
import {BehaviorSubject, combineLatest, Observable} from 'rxjs';
import {ApmModelType, ApmModelTypeLabel, LiveService} from '@core/interfaces/live/live';
import {map, tap} from 'rxjs/operators';
import {FormatsService} from '@core/utils';
import {TimeLocalizePipe} from '@theme/pipes';
import {GeneralOption} from '@core/interfaces/system/system-common';

@Injectable()
export class LiveSearchStore {
    private headerFilterTouched = new BehaviorSubject<boolean>(true);
    readonly headerFilterTouched$: Observable<boolean> = this.headerFilterTouched.asObservable();

    public resetData = new BehaviorSubject<boolean>(true);
    readonly resetData$: Observable<boolean> = this.resetData.asObservable();

    public predictedElement = new BehaviorSubject<boolean>(false);
    readonly predictedElement$ = this.predictedElement.asObservable();

    public searchStringAssetId$ = new BehaviorSubject<string>('');
    public searchStringModel$ = new BehaviorSubject<string>('');
    public assetIds = new BehaviorSubject<string[]>([]);
    readonly assetIds$ = this.assetIds.asObservable();

    public modelOptions = new BehaviorSubject<GeneralOption[]>([]);
    readonly modelOptions$ = this.modelOptions.asObservable();

    private assetId: BehaviorSubject<string> = new BehaviorSubject<string>(null);
    private model: BehaviorSubject<ApmModelType> = new BehaviorSubject<ApmModelType>(null);
    private uploadTime: BehaviorSubject<Date> = new BehaviorSubject<Date>(null);
    private customUploadTime: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    readonly assetId$: Observable<string> = this.assetId.asObservable();
    readonly model$: Observable<ApmModelType> = this.model.asObservable();
    uploadTime$: Observable<Date> = combineLatest<Observable<Date>, Observable<boolean>>([
        this.uploadTime.asObservable(),
        this.customUploadTime.asObservable(),
    ]).pipe(
        map(([uploadTime, customUploadTime]: [Date, boolean]) => {
            return customUploadTime ? uploadTime : new Date();
        }),
    );
    readonly customUploadTime$: Observable<boolean> = this.customUploadTime.asObservable();

    constructor(private liveService: LiveService, private localtimePipe: TimeLocalizePipe) {}

    searchAssetId(assetId: string) {
        this.liveService
            .getSearchAssetIds(
                assetId,
                this.model.value,
                this.localtimePipe.transform(this.getUploadTime(), FormatsService.DEFAULT_DATE_FORMAT_LUXON),
            )
            .pipe(
                map((data) => data.response),
                tap((assetIds) => {
                    this.assetIds.next(assetIds);
                    this.searchStringAssetId$.next(assetId);
                }),
            )
            .subscribe();
    }

    searchModel(model: string) {
        this.liveService
            .getSearchModels(
                this.assetId.value,
                model,
                this.localtimePipe.transform(this.getUploadTime(), FormatsService.DEFAULT_DATE_FORMAT_LUXON),
            )
            .pipe(
                map((data) => data.response),
                tap((models: string[]) => {
                    this.modelOptions.next(
                        models.map((code: string) => ({
                            label: ApmModelTypeLabel[code],
                            value: code,
                        })),
                    );
                    this.searchStringModel$.next(model);
                }),
            )
            .subscribe();
    }

    public setAssetId(assetId: string) {
        this.assetId.next(assetId);
    }

    public setModel(model: ApmModelType) {
        this.model.next(model);
    }

    public setCustomUploadTime(customUploadTime: boolean): void {
        this.customUploadTime.next(customUploadTime);
        // Set initial value to current time
        if (customUploadTime && this.uploadTime.value == null) {
            this.setUploadTime(new Date());
        }
    }

    public setUploadTime(uploadTime: any) {
        this.uploadTime.next(uploadTime);
    }

    public getAssetId() {
        return this.assetId.value;
    }

    public getModel() {
        return this.model.value;
    }

    public getCustomUploadTime() {
        return this.customUploadTime.value;
    }

    public getUploadTime() {
        if (this.getCustomUploadTime()) return this.uploadTime.value;
        return new Date();
    }

    public setPredictedElement(value: boolean) {
        this.predictedElement.next(value);
    }

    public getPredictedElement() {
        return this.predictedElement.value;
    }

    public getHeaderFilterTouched(): boolean {
        return this.headerFilterTouched.value;
    }

    public setHeaderFilterTouched(val: boolean) {
        this.headerFilterTouched.next(val);
    }
    public resetHeaderValues(): void {
        this.setAssetId(null);
        this.setModel(null);
        this.setUploadTime(null);
        this.setCustomUploadTime(false);
    }
}
